import {
    Platform
} from "react-native";
import type {
    TreeViewNextCheckedKeysConstantType,
    TreeViewAncestorTitlesConstantType,
    TreeViewCheckedStatesConstantType,
    TreeViewTypeaheadConstantType,
    TreeViewTabStopConstantType,
    TreeViewEntriesConstantType,
    TreeViewNodeConstantType,
    TreeViewCheckedState,
    TreeViewNodeItemType,
    TreeViewEntriesType,
    TreeViewEntryType,
    ITreeNode
} from "./type";

export const TREE_VIEW_NO_KEYS: Array<string> = [];

export const TREE_VIEW_NO_NODES: Array<ITreeNode> = [];

export const TREE_VIEW_ERROR_KEY_SUFFIX = "::error";

export const TREE_VIEW_PLACEHOLDER_COUNT = 3;

export const TREE_VIEW_ROOT_PLACEHOLDER_COUNT = 5;

export const TREE_VIEW_PLACEHOLDER_WIDTHS: Array<`${number}%`> = [
    "56%",
    "72%",
    "40%"
];

export const TREE_VIEW_PATH_SEPARATOR = " › ";

export const TREE_VIEW_ARROW_DOWN_KEY = "ArrowDown";

export const TREE_VIEW_ARROW_UP_KEY = "ArrowUp";

export const TREE_VIEW_CONTEXT_MENU_KEY = "ContextMenu";

export const TREE_VIEW_FOCUS_OPTIONS = {
    preventScroll: false
};

export const TREE_VIEW_POINTER_FOCUS_OPTIONS = {
    preventScroll: true
};

export const TREE_VIEW_PART_TAB_INDEX = Platform.OS === "web" ? -1 : undefined;

export const TREE_VIEW_ITEM_SELECTOR = "[role=\"treeitem\"]";

export const TREE_VIEW_MENU_SURFACE_SELECTOR = "[data-ncore-surface=\"popover\"]";

export const TREE_VIEW_TYPEAHEAD_KEY_LENGTH = 1;

export const TREE_VIEW_ASSISTIVE_ACTIONS = {
    longpress: "longpress",
    collapse: "collapse",
    expand: "expand"
};

export const getTreeViewIsBranch = ({
    isLazyEnabled,
    node
}: TreeViewNodeConstantType): boolean => {
    if(node.isLeaf) {
        return false;
    }

    return node.children !== undefined || isLazyEnabled;
};

export const getTreeViewIsLazy = ({
    isLazyEnabled,
    node
}: TreeViewNodeConstantType): boolean => {
    return !node.isLeaf && node.children === undefined && isLazyEnabled;
};

export const getTreeViewNodeMap = (nodes: Array<ITreeNode>): Map<string, TreeViewNodeItemType> => {
    const nodeMap = new Map<string, TreeViewNodeItemType>();

    const walk = (currentNodes: Array<ITreeNode>, parentKey: string | null) => {
        currentNodes.forEach((node) => {
            nodeMap.set(node.key, {
                parentKey,
                node
            });

            if(node.children) {
                walk(node.children, node.key);
            }
        });
    };

    walk(nodes, null);

    return nodeMap;
};

export const getTreeViewErrorEntry = ({
    level,
    node
}: {
    node: ITreeNode;
    level: number;
}): TreeViewEntryType => {
    return {
        key: `${node.key}${TREE_VIEW_ERROR_KEY_SUFFIX}`,
        isDrillTarget: false,
        parentKey: node.key,
        isDisabled: false,
        isExpanded: false,
        isLoading: false,
        isBranch: false,
        isError: true,
        isLazy: false,
        kind: "error",
        posInSet: 1,
        setSize: 1,
        level,
        node
    };
};

export const getTreeViewEntries = ({
    maxIndentLevel,
    isLazyEnabled,
    expandedKeys,
    loadingKeys,
    errorKeys,
    isDrill,
    nodes
}: TreeViewEntriesConstantType): TreeViewEntriesType => {
    const childrenByParent = new Map<string | null, Array<TreeViewEntryType>>();

    const entries: Array<TreeViewEntryType> = [];

    const walk = (currentNodes: Array<ITreeNode>, parentKey: string | null, level: number) => {
        const siblings: Array<TreeViewEntryType> = [];

        childrenByParent.set(parentKey, siblings);

        currentNodes.forEach((node, index) => {
            const isBranch = getTreeViewIsBranch({
                isLazyEnabled,
                node
            });

            const isLazy = getTreeViewIsLazy({
                isLazyEnabled,
                node
            });

            const isDrillTarget = isDrill && isBranch && level >= maxIndentLevel;

            const isExpanded = isBranch && !isDrillTarget && expandedKeys.has(node.key);

            const entry: TreeViewEntryType = {
                isLoading: isBranch && !isDrillTarget && (!!node.isLoading || loadingKeys.has(node.key)),
                isError: isBranch && !isDrillTarget && errorKeys.has(node.key),
                isDisabled: !!node.isDisabled,
                setSize: currentNodes.length,
                posInSet: index + 1,
                isDrillTarget,
                key: node.key,
                kind: "node",
                isExpanded,
                parentKey,
                isBranch,
                isLazy,
                level,
                node
            };

            entries.push(entry);

            siblings.push(entry);

            if(!isExpanded) {
                return;
            }

            if(entry.isError) {
                const errorEntry = getTreeViewErrorEntry({
                    level: level + 1,
                    node
                });

                entries.push(errorEntry);

                childrenByParent.set(node.key, [
                    errorEntry
                ]);

                return;
            }

            if(!node.children?.length) {
                childrenByParent.set(node.key, []);

                return;
            }

            walk(node.children, node.key, level + 1);
        });
    };

    walk(nodes, null, 1);

    return {
        childrenByParent,
        entries
    };
};

export const getTreeViewCheckedStates = ({
    checkBehaviour,
    selectedKeys,
    nodes
}: TreeViewCheckedStatesConstantType): Map<string, TreeViewCheckedState> => {
    const states = new Map<string, TreeViewCheckedState>();

    const walk = (node: ITreeNode): TreeViewCheckedState => {
        const children = node.children ?? [];

        const childStates = children.map((child) => ({
            isDisabled: !!child.isDisabled,
            state: walk(child)
        }));

        const countedStates = childStates.filter((item) => !item.isDisabled).map((item) => item.state);

        let state: TreeViewCheckedState = selectedKeys.has(node.key) ? "checked" : "unchecked";

        if(checkBehaviour === "cascade" && countedStates.length) {
            if(countedStates.every((item) => item === "checked")) {
                state = "checked";
            } else if(countedStates.every((item) => item === "unchecked")) {
                state = "unchecked";
            } else {
                state = "indeterminate";
            }
        }

        states.set(node.key, state);

        return state;
    };

    nodes.forEach((node) => {
        walk(node);
    });

    return states;
};

export const getTreeViewNextCheckedKeys = ({
    checkBehaviour,
    selectedKeys,
    isChecked,
    nodes,
    node
}: TreeViewNextCheckedKeysConstantType): Array<string> => {
    const nextKeys = new Set(selectedKeys);

    const apply = (current: ITreeNode) => {
        if(current.isDisabled) {
            return;
        }

        if(isChecked) {
            nextKeys.add(current.key);
        } else {
            nextKeys.delete(current.key);
        }

        if(checkBehaviour === "cascade") {
            current.children?.forEach(apply);
        }
    };

    const normalize = (current: ITreeNode) => {
        current.children?.forEach(normalize);

        const countedChildren = (current.children ?? []).filter((child) => !child.isDisabled);

        if(current.isDisabled || !countedChildren.length) {
            return;
        }

        if(countedChildren.every((child) => nextKeys.has(child.key))) {
            nextKeys.add(current.key);
        } else {
            nextKeys.delete(current.key);
        }
    };

    apply(node);

    if(checkBehaviour === "cascade") {
        nodes.forEach(normalize);
    }

    return Array.from(nextKeys);
};

export const getTreeViewEnabledKeys = (nodes: Array<ITreeNode>): Array<string> => {
    const keys: Array<string> = [];

    const walk = (currentNodes: Array<ITreeNode>) => {
        currentNodes.forEach((node) => {
            if(node.isDisabled) {
                return;
            }

            keys.push(node.key);

            if(node.children) {
                walk(node.children);
            }
        });
    };

    walk(nodes);

    return keys;
};

export const getTreeViewPath = ({
    nodeMap,
    keys
}: {
    nodeMap: Map<string, TreeViewNodeItemType>;
    keys: Array<string>;
}): Array<ITreeNode> => {
    return keys.reduce<Array<ITreeNode>>((path, key) => {
        const item = nodeMap.get(key);

        return item ? [
            ...path,
            item.node
        ] : path;
    }, []);
};

export const getTreeViewAncestorTitles = ({
    nodeMap,
    key
}: TreeViewAncestorTitlesConstantType): Array<string> => {
    const titles: Array<string> = [];

    let parentKey = nodeMap.get(key)?.parentKey ?? null;

    while(parentKey) {
        const item = nodeMap.get(parentKey);

        if(!item) {
            break;
        }

        titles.unshift(item.node.title);

        parentKey = item.parentKey;
    }

    return titles;
};

export const normalizeTreeViewText = ({
    locale,
    text
}: {
    locale: string;
    text: string;
}): string => {
    try {
        return text.trim().toLocaleLowerCase(locale);
    } catch {
        return text.trim().toLowerCase();
    }
};

export const getTreeViewTypeaheadIndex = ({
    startIndex,
    entries,
    locale,
    query
}: TreeViewTypeaheadConstantType): number => {
    const queryChars = Array.from(query);

    const isRepeat = queryChars.length > 1 && queryChars.every((char) => char === queryChars[0]);

    const prefix = normalizeTreeViewText({
        text: isRepeat ? queryChars[0] ?? query : query,
        locale
    });

    const from = queryChars.length === 1 || isRepeat ? startIndex + 1 : Math.max(startIndex, 0);

    for(let offset = 0; offset < entries.length; offset++) {
        const index = (from + offset) % entries.length;

        const entry = entries[index];

        if(entry?.kind !== "node") {
            continue;
        }

        const title = normalizeTreeViewText({
            text: entry.node.title,
            locale
        });

        if(title.startsWith(prefix)) {
            return index;
        }
    }

    return -1;
};

export const getTreeViewTabStopKey = ({
    selectedKeys,
    focusedKey,
    entries
}: TreeViewTabStopConstantType): string | null => {
    if(focusedKey && entries.some((entry) => entry.key === focusedKey)) {
        return focusedKey;
    }

    const selectedEntry = entries.find((entry) => entry.kind === "node" && selectedKeys.has(entry.key));

    return selectedEntry?.key ?? entries[0]?.key ?? null;
};

export const getTreeViewElement = (node: unknown) => {
    if(Platform.OS !== "web" || typeof HTMLElement === "undefined" || !(node instanceof HTMLElement)) {
        return null;
    }

    return node;
};

export const getIsTreeViewKeyTarget = ({
    target,
    node
}: {
    target: EventTarget | null;
    node: unknown;
}): boolean => {
    const element = getTreeViewElement(node);

    if(!element || !(target instanceof Node)) {
        return false;
    }

    return element.contains(target);
};

export const getIsTreeViewPartTarget = ({
    target,
    node
}: {
    target: unknown;
    node: unknown;
}): boolean => {
    const item = getTreeViewElement(node);

    const element = getTreeViewElement(target);

    if(!item || !element || element === item) {
        return false;
    }

    return element.closest(TREE_VIEW_ITEM_SELECTOR) === item;
};

export const getIsTreeViewMenuFocusLost = (): boolean => {
    if(Platform.OS !== "web" || typeof document === "undefined") {
        return false;
    }

    const activeElement = getTreeViewElement(document.activeElement);

    if(!activeElement || activeElement === document.body) {
        return true;
    }

    return !!activeElement.closest(TREE_VIEW_MENU_SURFACE_SELECTOR);
};

export const focusTreeViewElement = (node: unknown, options = TREE_VIEW_FOCUS_OPTIONS) => {
    if(typeof node !== "object" || node === null || !("focus" in node) || typeof node.focus !== "function") {
        return;
    }

    node.focus(options);
};
